import {apiConfig as config} from "./index.js";
import AsyncLock from "async-lock";

type service = "core" | "comments" | "files" | "unknown";
type statusCodeHandlers = Record<number, Function>;
type errorCodeMsgMap = Record<number, Function>;

/**
 * This helps us handle errors internally.
 */
export class FetchError extends Error {
	response: Response;

	constructor(type: string, response: Response) {
		super(type);
		this.response = response;
	}
}

/**
 * This can be used by the outside to handle errors.
 */
export class ApiCommError extends Error {
	msg:        string;
	type:       string;
	service:    string;
	apiCode:    string | number;
	apiMessage: string;

	/**
	 * @param {string}        type
	 * @param {string}        service      E.g. "core", "auth", "comments", "files"
	 * @param {string}        message
	 * @param {string|number} [apiCode]    Specific error code returned by the API
	 * @param {string}        [apiMessage] Specific message returned by the API
	 *                                     (as opposed to "message", which is just shown to the user)
	 */
	constructor(type: string, service: service, message: string, apiCode: string | number = 0, apiMessage = "") {
		super(message);
		this.msg = message;
		this.type = type;
		this.service = service;
		this.apiCode = apiCode;
		this.apiMessage = apiMessage;
	}
}

/**
 * TransportError is thrown when a request failed due to a network issue.
 */
export class TransportError extends ApiCommError {
	constructor(message: string) {
		super("network", "unknown", message);
	}
}

/**
 * Helper function to make error handling a bit easier.
 *
 * @param {string}                    errorMsg
 * @param {Error}                     error
 * @param {Object.<number, function>} [errorCodeMsgMap]
 * @throws {ApiCommError}
 */
export async function handleError(errorMsg: string, error: any, errorCodeMsgMap: undefined) {
	throw await processError(errorMsg, error, errorCodeMsgMap);
}

function getService(url: string): service {
	if (url.startsWith(config.urls.pn_api))
		return "core";
	else if (url.startsWith(config.urls.pn_comments_api))
		return "comments";
	else if (url.startsWith(config.urls.pn_files_api))
		return "files";
	else
		return "unknown";
}

/**
 * Helper function to make error handling a bit easier.
 */
export async function processError(errorMsg: string, error: Error, errorCodeMsgMap: errorCodeMsgMap | undefined): Promise<ApiCommError> {
	console.error(errorMsg, error, error.message);

	let type = error.message;

	// From MDN (https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch):
	// A fetch() promise will reject with a TypeError when a network error is encountered
	// or CORS is misconfigured on the server-side, although this usually means permission
	// issues or similar — a 404 does not constitute a network error, for example.

	if (error instanceof TypeError)
		return new TransportError(errorMsg);
	else if (error instanceof ApiCommError) {
		// a statusCodeHandler already decided what to do,
		// so we just need to pass along the error.
		return error;
	} else {
		if (!(error instanceof FetchError))
			type = "unknown";

		let service: service = "unknown";
		let apiCode;
		let apiMessage;
		// @ts-ignore
		if (error.response) {
			// @ts-ignore
			const response = error.response;

			if (typeof response.url === "string" && response.url)
				service = getService(response.url);

			const httpStatus = response.status;
			if (httpStatus === 401)
				type = "auth";

			const body = (response instanceof Response ? await response.json() : response);
			if (body && typeof body === "object" && body.error && typeof body.error === "object" && (body.error.code || body.error.message)) {
				// Standard error format from pn-api
				apiCode = body.error.code;
				apiMessage = body.error.message;

				console.error("Extracted api error", {service, httpStatus, apiCode, apiMessage});

				if (errorCodeMsgMap?.[apiCode])
					errorMsg = await errorCodeMsgMap[apiCode](response, body);
			}
		}

		return new ApiCommError(type, service, errorMsg, apiCode, apiMessage);
	}
}

const lock = new AsyncLock({timeout: 300000, maxPending: 1000});

interface options {
	url:                URL;
	method?:            "GET" | "POST" | "PATCH" | "DELETE" | "PUT"; // Defaults to GET
	keepalive?:         boolean; // See Fetch API documentation
	body?:              any;
	headers?:           Record<string, string>;
	expectedStatusCode: number; // E.g. 201
	errorMessage:       string; // E.g. "Error changing annotation topic."
	isJsonBody?:        boolean; // If true, sends the body in JSON format.
	expectedJson?:      boolean; // If true, expects the response to be in JSON format.

	/**
	 * Async functions, keyed by status code. If passed, makeApiCall() returns whatever the handlers return.
	 * In this case, "expectedJson" only has effect on the "expectedStatusCode"; others receive the unprocessed
	 * response object.
	 */
	statusCodeHandlers?: statusCodeHandlers;

	/**
	 * [Async] functions, keyed by error code. If passed, makeApiCall() will check for a match to an api error
	 * code in this map, invoke the matching function, and use the result as the error object message (rather
	 * than options.errorMessage).
	 */
	errorCodeMsgMap?: errorCodeMsgMap;
};

const defaultOptions = {
	method: "GET"
};

/**
 * @param {options} options
 * @param {function} caller Used to override the underlying function (for unit tests).
 * @return {Promise<*>}
 * @throws {ApiCommError}
 */
export async function makeApiCall(options: options, caller = _makeApiCall) {
	options = Object.assign({}, defaultOptions, options); // add defaults
	const service = getService(options.url.toString());

	if (options.method === "GET") {
		// Wait until the lock is free but release it immediately (before making the API call).
		// This allows GET calls to be done in parallel.
		await lock.acquire(service, () => {});
		return caller(options);
	} else {
		// For any non-GET calls, lock until the API returns.
		return lock.acquire(service, () => caller(options));
	}
}

async function _makeApiCall({
	url, method, keepalive, body, headers, expectedStatusCode, errorMessage,
	isJsonBody, expectedJson, statusCodeHandlers, errorCodeMsgMap
}: options) {
	const finalBody = isJsonBody ? JSON.stringify(body) : body;
	const finalHeaders = isJsonBody ? {"Content-Type": "application/json", ...headers} : headers;

	try {
		const response = await fetch(
			// @ts-ignore - Not sure why this says "TS2345: Argument of type 'URL' is not assignable to parameter of type 'RequestInfo'."
			url,
			{
				method:    method ? method : "GET",
				body:      finalBody,
				headers:   finalHeaders,
				keepalive: !!keepalive
			}
		);

		if (statusCodeHandlers?.[response.status] !== undefined) {
			const param = (response.status === expectedStatusCode && expectedJson)
				? await response.json()
				: response;

			return await statusCodeHandlers[response.status](param);
		}

		if (response.status === 401 && config.authFailHandler)
			setTimeout(config.authFailHandler, 0);

		if (response.status !== expectedStatusCode)
			throw new FetchError("unknown", response);

		return expectedJson ? await response.json() : response;
	} catch (error) {
		throw await processError(errorMessage, error, errorCodeMsgMap);
	}
}
